import React, { useCallback } from 'react';
import { DraftSelectionInteraction, DraftTaskSelection } from './types';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useDraftGestures = (state: PlannerState, refs: PlannerRefs) => {
    const {
        draftInteraction, draftSelection, setDraftInteraction, setDraftSelection, timeSlotHeight,
    } = state;
    const { timelineRef } = refs;
    const getDraftStartDate = useCallback((selection: DraftTaskSelection) => {
        const [hours, minutes] = selection.startTime.split(':').map(Number);
        const date = new Date(selection.date);
        date.setHours(hours, minutes, 0, 0);
        return date;
    }, []);
    const getDraftSelectionFromDate = useCallback((date: Date, duration: number): DraftTaskSelection => ({
        date: new Date(date),
        startTime: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
        startOffset: date.getMinutes() / 60 * timeSlotHeight,
        duration,
    }), [timeSlotHeight]);
    const getDraftSelectionForPointer = useCallback((interaction: DraftSelectionInteraction, clientY: number) => {
        const scrollDelta = timelineRef.current
            ? timelineRef.current.scrollTop - interaction.startScrollTop : 0;
        const deltaMinutes = Math.round((clientY - interaction.startY + scrollDelta)
            / (timeSlotHeight / 60) / 15) * 15;
        const start = getDraftStartDate(interaction.startSelection);
        if (interaction.mode === 'drag') {
            start.setMinutes(start.getMinutes() + deltaMinutes);
            return getDraftSelectionFromDate(start, interaction.startSelection.duration);
        }
        if (interaction.edge === 'top') {
            const actual = Math.min(deltaMinutes, interaction.startSelection.duration - 30);
            start.setMinutes(start.getMinutes() + actual);
            return getDraftSelectionFromDate(start, interaction.startSelection.duration - actual);
        }
        return { ...interaction.startSelection, duration: Math.max(30, interaction.startSelection.duration + deltaMinutes) };
    }, [getDraftSelectionFromDate, getDraftStartDate, timeSlotHeight, timelineRef]);
    const handleDraftPointerDown = (event: React.PointerEvent) => {
        if (!draftSelection || !(event.target instanceof HTMLElement)
            || event.target.closest('button') || event.target.closest('.resize-handle')) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDraftInteraction({
            mode: 'drag', startY: event.clientY, startScrollTop: timelineRef.current?.scrollTop || 0,
            startSelection: draftSelection,
        });
    };
    const handleDraftResizePointerDown = (edge: 'top' | 'bottom', event: React.PointerEvent) => {
        if (!draftSelection) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDraftInteraction({
            mode: 'resize', edge, startY: event.clientY,
            startScrollTop: timelineRef.current?.scrollTop || 0, startSelection: draftSelection,
        });
    };
    const handleDraftPointerMove = useCallback((event: PointerEvent | React.PointerEvent) => {
        if (draftInteraction) setDraftSelection(getDraftSelectionForPointer(draftInteraction, event.clientY));
    }, [draftInteraction, getDraftSelectionForPointer, setDraftSelection]);
    const handleDraftPointerUp = useCallback(() => {
        if (draftInteraction) setDraftInteraction(null);
    }, [draftInteraction, setDraftInteraction]);
    return {
        getDraftSelectionForPointer, handleDraftPointerDown, handleDraftPointerMove,
        handleDraftPointerUp, handleDraftResizePointerDown,
    };
};
